import { Edit } from "@mui/icons-material";
import { IconButton, Switch, Typography } from "@mui/material";
import { ChangeEvent, useCallback } from "react";

import { useUpdateMedicationMutation } from "src/api/services/medicationsApi";
import { Medication, Column } from "src/api/types";
import { theme } from "src/assets/theme";
import { useMedicationsDrawer } from "src/context/drawers/MedicationsDrawerProvider/useMedicationsDrawer";
import { capitalize } from "src/utils/capitalize";

export const useColumns = (): Column<Medication>[] => {
  const [updateMedication] = useUpdateMedicationMutation();
  const { handleOpenUpdateMedication } = useMedicationsDrawer();

  const handleToggle = useCallback(
    (event: ChangeEvent<HTMLInputElement>, id: number) => {
      updateMedication({
        id,
        body: {
          active: event.target.checked,
        },
      });
    },
    [updateMedication],
  );

  return [
    {
      id: "edit",
      label: "Edit",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <IconButton onClick={() => handleOpenUpdateMedication(row)}>
          <Edit sx={{ color: theme.palette.secondary.main }} />
        </IconButton>
      ),
    },
    {
      id: "id",
      label: "ID",
      minWidth: 30,
      orderBy: "id",
      align: "center",
      format: (row) => row.id.toString(),
      render: (row) => <Typography>{row.id}</Typography>,
      orderable: true,
    },
    {
      id: "name",
      label: "Name",
      minWidth: 120,
      align: "center",
      format: (row) => row.name,
      render: (row) => <Typography>{row.name}</Typography>,
      orderable: false,
    },
    {
      id: "strength",
      label: "Strength",
      minWidth: 120,
      align: "center",
      format: (row) => row.strength,
      render: (row) => <Typography>{row.strength}</Typography>,
      orderable: false,
    },
    {
      id: "dispense_unit",
      label: "Dispense Unit",
      minWidth: 120,
      align: "center",
      format: (row) => capitalize(row.dispense_unit),
      render: (row) => <Typography>{capitalize(row.dispense_unit)}</Typography>,
      orderable: false,
    },
    {
      id: "total_dose",
      label: "Total Dose",
      minWidth: 120,
      align: "center",
      format: (row) => row.dosage,
      render: (row) => <Typography>{row.dosage}</Typography>,
      orderable: false,
    },
    {
      id: "treatment",
      label: "Treatment",
      minWidth: 120,
      align: "center",
      format: (row) => row.treatment.name,
      render: (row) => <Typography>{row.treatment.name}</Typography>,
      orderable: false,
    },
    {
      id: "gender",
      label: "Gender",
      minWidth: 120,
      align: "center",
      format: (row) => row.gender ?? "N/A",
      render: (row) => <Typography>{row.gender ?? "N/A"}</Typography>,
      orderable: false,
    },
    {
      id: "form",
      label: "Form",
      minWidth: 120,
      align: "center",
      format: (row) => capitalize(row.form),
      render: (row) => <Typography>{capitalize(row.form)}</Typography>,
      orderable: false,
    },
    {
      id: "pharmacy",
      label: "Pharmacy",
      minWidth: 100,
      align: "center",
      format: (row) => row.pharmacy.name,
      render: (row) => <Typography>{row.pharmacy.name}</Typography>,
      orderable: false,
    },
    {
      id: "drug_id",
      label: "Drug ID",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (row) => row.drug_id.toString() ?? "",
      render: (row) => {
        return <Typography>{row.drug_id}</Typography>;
      },
    },
    {
      id: "ndc",
      label: "NDC",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (row) => row.ndc?.toString() ?? "",
      render: (row) => {
        if (row.ndc == null) {
          return <Typography>N/A</Typography>;
        }

        return <Typography>{row.ndc}</Typography>;
      },
    },
    {
      id: "toggle",
      label: "Active",
      minWidth: 120,
      align: "center",
      format: (row) => row.active.toString(),
      render: (row) => (
        <Switch
          color="secondary"
          checked={row.active}
          disabled={!row.pharmacy.active}
          onChange={(e) => handleToggle(e, row.id)}
        />
      ),
      orderable: false,
    },
  ];
};
